<template>
    <div
        class="sbs-stroke-loader"
        :style="loaderStyles">
        <svg
            class="sbs-stroke-loader__svg"
            :viewBox="loaderViewboxSize"
            fill="none">
            <rect
                v-if="strokeTrackTheme"
                v-bind="strokeRectData"
                class="trs"
                :class="strokeTrackClasses" />
            <rect
                v-bind="strokeRectData"
                class="trs"
                :class="strokeRectClasses"
                :stroke-dasharray="strokeRectDasharray"
                :stroke-dashoffset="strokeRectDashoffset" />
        </svg>

        <!-- @slot Слот по-умолчанию -->
        <slot></slot>
    </div>
</template>

<script>
import ResizeObserver from '../../utils/resizeObserver.js';
import { AllColors } from '../../utils/colors.js';
import { converToUnit } from '../../utils/helpers.js';

const COMPLETED_PROGRESS = 100;

/**
 * Бордерный прелоадер
 */
export default {
    name: 'SbsStrokeLoader',
    data() {
        return {
            loaderWidth: 0,
            loaderHeight: 0,
            resizeObserver: null,
        };
    },
    computed: {
        loaderStyles() {
            return {
                borderRadius: this.computedRadius
                    ? converToUnit(this.computedRadius + this.computedStrokeWidth / 2)
                    : 0,
            };
        },
        loaderViewboxSize() {
            return `0 0 ${this.loaderWidth} ${this.loaderHeight}`;
        },
        strokeTrackClasses() {
            const classes = [];

            if (this.strokeTrackTheme) {
                classes.push(`stroke-${this.strokeTrackTheme}`);
            }

            return classes;
        },
        strokeRectClasses() {
            const classes = [];

            if (this.strokeTheme) {
                classes.push(`stroke-${this.strokeTheme}`);
            }

            return classes;
        },
        strokeRectWidth() {
            return Math.max(this.loaderWidth - this.computedStrokeWidth, 0);
        },
        strokeRectHeight() {
            return Math.max(this.loaderHeight - this.computedStrokeWidth, 0);
        },
        computedRadius() {
            if (this.borderRadius <= 0) return 0;

            const minSide = Math.min(
                this.strokeRectWidth,
                this.strokeRectHeight,
            );
            const maxRadius = minSide / 2;

            return Math.min(this.borderRadius, maxRadius);
        },
        computedStrokeWidth() {
            if (this.strokeWidth <= 0) return 0;

            const minSide = Math.min(
                this.loaderWidth,
                this.loaderHeight,
            );
            const maxStrokeWidth = minSide / 2;

            return Math.min(this.strokeWidth, maxStrokeWidth);
        },
        strokeRectData() {
            return {
                'x': this.computedStrokeWidth / 2,
                'y': this.computedStrokeWidth / 2,
                'width': this.strokeRectWidth,
                'height': this.strokeRectHeight,
                'rx': this.computedRadius,
                'stroke-width': this.computedStrokeWidth,
                'fill': 'none',
            };
        },
        strokeRectPerimeter() {
            const CORNERS_NUMBER = 4;
            // Считаем длину одного закругления.
            // Это четверть от длины окружности с переданным радиусом минус половина тощины обводки
            const arcLength =
                (2 * Math.PI * this.computedRadius) /
                CORNERS_NUMBER;
            // Считаем ширину без закруглений минус половина тощины обводки
            const widthLength = this.strokeRectWidth - 2 * this.computedRadius;
            // Считаем высоту без закруглений минус половина тощины обводки
            const heightLength = this.strokeRectHeight - 2 * this.computedRadius;

            // Периметр равен сумме длин 4 закруглений и 2 длин ширины и высоты
            return CORNERS_NUMBER * arcLength + 2 * (widthLength + heightLength);
        },
        computedProgress() {
            const normalizedProgress = Math.max(
                this.progress / COMPLETED_PROGRESS,
                0,
            );

            return this.strokeRectPerimeter * normalizedProgress;
        },
        strokeRectDasharray() {
            const strokeLength = this.computedProgress;
            const spaceLength = this.strokeRectPerimeter - this.computedProgress;

            return `${strokeLength} ${spaceLength}`;
        },
        strokeRectDashoffset() {
            return Math.min(
                -(this.strokeRectWidth / 2 - this.computedRadius),
                0,
            );
        },
    },
    methods: {
        calcLoaderSize() {
            this.loaderWidth = this.$el.offsetWidth;
            this.loaderHeight = this.$el.offsetHeight;
        },
    },
    inheritAttrs: false,
    props: {
        /**
         * Радиус полосы загрузки
         */
        borderRadius: {
            type: Number,
            default: 0,
        },
        /**
         * Прогресс загрузки от 0 до 100
         */
        progress: {
            type: Number,
            default: 0,
        },
        /**
         * Цвет полосы загрузки
         */
        strokeTheme: {
            type: String,
            default: AllColors.PRIMARY,
        },
        /**
         * Цвет подложки полосы загрузки
         */
        strokeTrackTheme: {
            type: String,
            default: '',
        },
        /**
         * Толщина полосы загрузки
         */
        strokeWidth: {
            type: Number,
            default: 2,
        },
    },
    created() {
        this.resizeObserver = new ResizeObserver(this.calcLoaderSize);
    },
    async mounted() {
        this.calcLoaderSize();
        await this.$nextTick();

        if (this.$el) {
            this.resizeObserver.observe(this.$el);
        }
    },
    beforeUnmount() {
        this.resizeObserver.disconnect();
    },
};
</script>

<style lang="scss">
.sbs-stroke-loader {
    position: relative;
    display: inline-flex;
    flex-direction: column;
    flex-shrink: 0;
    min-width: 52px;
    min-height: 52px;
    overflow: hidden;
    mask-image: radial-gradient(white, black);

    &__svg {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 1;
        pointer-events: none;
    }
}
</style>
